import { h, clear, onTap, fmtTime, fmtClock } from './dom';
import type { Game } from '../Game';
import type { HammerOption } from '../systems/Building';
import { itemDef, type ArmorSlot } from '../data/items';
import { difficulties } from '../data/difficulty';
import { MAX_HUNGER, MAX_STAMINA } from '../player/Stats';
import { HOTBAR_SIZE, type Inventory, type Slot } from '../player/Inventory';
import { availableRecipes, craftFromInventory, craftFromGrid, findRecipeForGrid, emptyGrid, repairEquipment, type CraftGrid } from '../systems/Crafting';
import { QUALITY_PROFILES, type QualityLevel } from '../core/Settings';
import { AdminClient } from '../net/AdminClient';
import type { Station } from '../data/recipes';

/**
 * Arayüz.
 * Tek bir kök element altında ekranlar ve overlay'ler yönetilir.
 * Mobil ve PC aynı düzeni paylaşır; dokunma alanları büyük tutulur,
 * PC'de klavye kısayolları ek olarak çalışır.
 */

type ScreenId = 'boot' | 'menu' | 'admin' | 'mode' | 'worlds' | 'create' | 'settings' | 'profile' | 'report' | 'dead';

export class UI {
  private screens = new Map<ScreenId, HTMLElement>();
  private hud!: HTMLElement;
  private toasts!: HTMLElement;
  private modal!: HTMLElement;
  private modalCard!: HTMLElement;
  private hotbarEl!: HTMLElement;
  private hudRefs: Record<string, HTMLElement> = {};
  private craftGrid: CraftGrid = emptyGrid();
  private pendingMove: number | null = null;
  private pendingChest: number | null = null;
  private currentModal: 'none' | 'inventory' | 'craft' | 'hammer' | 'pause' | 'chest' | 'shop' = 'none';
  private hammerContext: { structureId: string; options: HammerOption[] } | null = null;
  private chestId: string | null = null;
  private craftStation: Station = 'hand';
  private readonly admin = new AdminClient();

  constructor(private game: Game, private root: HTMLElement) {
    this.buildBoot();
    this.buildMenu();
    this.buildAdmin();
    this.buildMode();
    this.buildWorlds();
    this.buildCreate();
    this.buildSettings();
    this.buildProfile();
    this.buildReport();
    this.buildDead();
    this.buildHud();
    this.buildModal();
    this.root.append(this.game.touch.root);

    this.bindGame();
    this.showScreen('boot');
    this.runIntro();
  }

  // ---------------------------------------------------------------- iskelet

  private screen(id: ScreenId, children: Array<Node | string>): HTMLElement {
    const el = h('div', { class: 'screen', id: `screen-${id}` }, children);
    this.screens.set(id, el);
    this.root.append(el);
    return el;
  }

  showScreen(id: ScreenId | null): void {
    for (const [key, el] of this.screens) el.classList.toggle('active', key === id);
  }

  private bindGame(): void {
    this.game.events.on('toast', ({ text, kind }) => this.toast(text, kind));
    this.game.events.on('hud:dirty', () => this.updateHud());
    this.game.events.on('inventory:dirty', () => {
      this.updateHotbar();
      if (this.currentModal === 'inventory' || this.currentModal === 'craft') this.renderModalBody();
    });
    this.game.events.on('state:changed', ({ state }) => {
      this.hud.classList.toggle('active', state === 'playing' || state === 'paused');
      if (state === 'menu') this.showScreen('menu');
      if (state === 'playing') this.showScreen(null);
      if (state === 'dead') this.showScreen('dead');
    });
    this.game.events.on('player:died', ({ cause }) => {
      const el = this.screens.get('dead')!.querySelector('.cause');
      if (el) el.textContent = cause;
    });
    this.game.events.on('hammer:open', (ctx) => {
      this.hammerContext = ctx;
      this.openModal('hammer');
    });
    this.game.events.on('shop:open', () => this.openModal('shop'));
    this.game.events.on('combat:hurt', () => {
      this.flashHurt();
      this.pulseHpBar();
    });
    this.game.events.on('combat:hit', ({ killed }) => this.flashHitMarker(killed));
    this.game.events.on('quests:changed', () => {
      if (this.currentModal === 'shop') this.renderModalBody();
    });
    this.game.events.on('station:open', ({ station, structureId }) => {
      if (station === 'workbench' || station === 'furnace' || station === 'grinder') {
        this.craftStation = station;
        this.openModal('craft');
      } else {
        this.chestId = structureId;
        this.openModal('chest');
      }
    });
    // Klavye: envanter / menü
    this.game.input.events.on('pressed', ({ action }) => {
      if (action === 'inventory' && !this.game.isObserver) this.toggleModal('inventory');
      if (action === 'menu') this.onEscape();
    });
  }

  private onEscape(): void {
    // İlk Escape yalnız FPS yakalamasını bırakır; oyun akmaya devam eder ve
    // masaüstü kullanıcısı hotbar/HUD öğelerine imleçle erişebilir.
    if (this.game.input.isPointerLocked) {
      this.game.input.exitPointerLock();
      return;
    }
    if (this.currentModal !== 'none') {
      this.closeModal();
      return;
    }
    if (this.game.state === 'playing') this.openModal('pause');
  }

  // ---------------------------------------------------------------- açılış

  private buildBoot(): void {
    this.screen('boot', [
      h('div', { class: 'brand' }, [h('div', { class: 'logo', text: 'KG' }), h('div', { class: 'tag', text: 'HAYATTA KAL · ÜRET · KEŞFET' })]),
      h('div', { class: 'skip', text: 'Devam etmek için dokun' }),
    ]);
    onTap(this.screens.get('boot')!, () => this.finishIntro());
  }

  private introDone = false;
  private runIntro(): void {
    window.setTimeout(() => this.finishIntro(), 2600);
  }
  private finishIntro(): void {
    if (this.introDone) return;
    this.introDone = true;
    this.game.setState('menu');
    if (new URLSearchParams(location.search).get('admin') === '1') {
      void this.renderAdmin();
      this.showScreen('admin');
      return;
    }
    // Paylaşılan bağlantıyla gelindiyse doğrudan dünya oluşturma ekranına götür
    if (this.readInviteFromUrl()) {
      this.renderCreate();
      this.showScreen('create');
      this.toast('Paylaşılan dünya ayarları yüklendi', 'good');
      return;
    }
    this.showScreen('menu');
  }

  private buildMenu(): void {
    const btn = (text: string, fn: () => void, cls = 'btn') => {
      const b = h('button', { class: cls, text, type: 'button' });
      onTap(b, fn);
      return b;
    };
    this.screen('menu', [
      h('div', { class: 'brand' }, [h('div', { class: 'logo', text: 'KG' }), h('div', { class: 'tag', text: 'HAYATTA KAL · ÜRET · KEŞFET' })]),
      btn('OYNA', () => this.showScreen('mode'), 'btn primary'),
      btn('HESAP / PROFİL', () => {
        this.renderProfile();
        this.showScreen('profile');
      }),
      btn('AYARLAR', () => this.showScreen('settings')),
      btn('SORUN BİLDİR', () => this.showScreen('report')),
      h('div', { class: 'hint', text: `Sürüm: ${__KG_BUILD__}` }),
    ]);
  }

  private buildAdmin(): void {
    this.screen('admin', [h('h1', { text: 'KG YÖNETİM' }), h('div', { class: 'admin-content' })]);
  }

  private async renderAdmin(): Promise<void> {
    const content = this.screens.get('admin')!.querySelector('.admin-content') as HTMLElement;
    clear(content);
    try {
      await this.admin.session();
      await this.renderAdminDashboard(content);
    } catch {
      const username = h('input', { value: 'kg-admin-ug', readonly: true });
      const password = h('input', { type: 'password', autocomplete: 'current-password', placeholder: 'Yönetici şifresi' });
      const login = h('button', { class: 'btn primary', text: 'GİRİŞ', type: 'button' });
      onTap(login, async () => {
        try {
          await this.admin.login(username.value, password.value);
          password.value = '';
          await this.renderAdmin();
        } catch (error) { this.toast(String(error instanceof Error ? error.message : error), 'bad'); }
      });
      content.append(h('div', { class: 'card' }, [h('div', { class: 'field' }, [h('label', { text: 'Kullanıcı' }), username]), h('div', { class: 'field' }, [h('label', { text: 'Şifre' }), password]), login]));
    }
  }

  private async renderAdminDashboard(content: HTMLElement): Promise<void> {
    const controls = h('div', { class: 'row' });
    const refresh = h('button', { class: 'btn small', text: 'YENİLE', type: 'button' });
    const logout = h('button', { class: 'btn small danger', text: 'ÇIKIŞ', type: 'button' });
    onTap(refresh, () => void this.renderAdmin());
    onTap(logout, async () => { try { await this.admin.logout(); } finally { void this.renderAdmin(); } });
    controls.append(refresh, logout); content.append(controls);
    try {
      const [usersData, roomsData, auditData] = await Promise.all([this.admin.users(), this.admin.rooms(), this.admin.audit()]);
      const users = (usersData.users as Array<Record<string, unknown>>) ?? [];
      const rooms = (roomsData.rooms as Array<Record<string, unknown>>) ?? [];
      const audits = (auditData.audit as Array<Record<string, unknown>>) ?? [];
      const roomList = h('div', { class: 'card' }, [h('h2', { text: `Çevrimiçi Odalar (${rooms.length})` })]);
      for (const room of rooms) {
        const code = String(room.code ?? '');
        const observe = h('button', { class: 'btn small primary', text: 'GÖZLE', type: 'button' });
        onTap(observe, async () => { try { const result = await this.admin.ticket(code); this.game.observeRoom(code, String(result.ticket ?? '')); this.showScreen(null); } catch (error) { this.toast(String(error instanceof Error ? error.message : error), 'bad'); } });
        roomList.append(h('div', { class: 'stat-line' }, [h('b', { text: String(room.name ?? code) }), h('span', { text: ` · ${code} · ${Number(room.players ?? 0)} oyuncu · ${Number(room.observers ?? 0)} gözlemci ` }), observe]));
      }
      if (!rooms.length) roomList.append(h('p', { class: 'hint', text: 'Aktif oda yok.' }));
      const userList = h('div', { class: 'card' }, [h('h2', { text: `Kullanıcılar (${users.length}/100)` })]);
      for (const user of users) {
        userList.append(h('div', { class: 'stat-line' }, [h('b', { text: String(user.name ?? 'Gezgin') }), h('span', { text: ` · ${String(user.id ?? '')} · ${((user.worlds as unknown[]) ?? []).length} dünya` })]));
        for (const world of (user.worlds as Array<Record<string, unknown>>) ?? []) userList.append(h('p', { class: 'hint', style: 'text-align:left;margin:2px 0 6px 14px', text: `${String(world.name ?? '')} · ${String(world.mode ?? '')}/${String(world.difficulty ?? '')} · gün ${Number(world.day ?? 1)} · rev ${Number(world.rev ?? 0)}` }));
      }
      const auditList = h('div', { class: 'card' }, [h('h2', { text: `Denetim Kaydı (${audits.length}/100)` })]);
      for (const entry of audits) auditList.append(h('div', { class: 'stat-line', text: `${new Date(Number(entry.created_at ?? 0)).toLocaleString('tr-TR')} · ${String(entry.action ?? '')}${entry.target ? ` · ${String(entry.target)}` : ''}` }));
      content.append(roomList, userList, auditList);
    } catch (error) { content.append(h('p', { class: 'hint', text: String(error instanceof Error ? error.message : error) })); }
  }

  private buildMode(): void {
    const card = (title: string, desc: string, fn: () => void) => {
      const el = h('div', { class: 'card' }, [
        h('h2', { text: title }),
        h('p', { class: 'hint', text: desc }),
        h('div', { class: 'row', style: 'margin-top:12px' }, [(() => {
          const b = h('button', { class: 'btn primary', text: 'SEÇ', type: 'button' });
          onTap(b, fn);
          return b;
        })()]),
      ]);
      return el;
    };
    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen('menu'));
    this.screen('mode', [
      h('h1', { text: 'MOD SEÇ' }),
      card('HİKÂYELİ MOD', 'El yapımı, sınırlı harita. KG Vadisi, Kızıl Kale ve gizli bölgeler.', () => {
        this.prefill = { mode: 'story', name: 'KG Vadisi' };
        this.renderCreate();
        this.showScreen('create');
      }),
      card('NORMAL MOD', 'Seed ile üretilen çok büyük procedural dünya. Sınırsız keşif.', () => {
        this.prefill = { mode: 'normal', name: '' };
        this.renderCreate();
        this.showScreen('create');
      }),
      (() => {
        const b = h('button', { class: 'btn', text: 'DÜNYALARIM', type: 'button' });
        onTap(b, () => {
          void this.renderWorlds();
          this.showScreen('worlds');
        });
        return b;
      })(),
      back,
    ]);
  }

  private prefill: { mode: 'story' | 'normal'; name: string; seed?: string; difficulty?: string } = { mode: 'normal', name: '' };

  /** Dünya ayarlarını bağlantı olarak paylaşır (seed + mod + zorluk). */
  private async shareWorld(w: { name: string; mode: string; difficulty: string; seed: number }): Promise<void> {
    const url = new URL(location.href);
    url.hash = '';
    url.search = new URLSearchParams({
      seed: String(w.seed),
      mode: w.mode,
      diff: w.difficulty,
      name: w.name,
    }).toString();
    const link = url.toString();
    try {
      // Mobilde yerel paylaşım penceresi, masaüstünde panoya kopyala
      if (navigator.share) {
        await navigator.share({ title: `KG · ${w.name}`, text: 'Aynı dünyada oyna:', url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      this.toast('Bağlantı kopyalandı', 'good');
    } catch {
      // İzin verilmediyse en azından göster
      this.toast(`Seed: ${w.seed}`, 'info');
      window.prompt('Bu bağlantıyı paylaş:', link);
    }
  }

  /**
   * Bağlantıdaki dünya ayarlarını okur.
   * Biri paylaşılan adresi açtığında aynı seed/mod/zorluk ile dünya kurabilir;
   * böylece ikisi de birebir aynı araziyi ve kaynak yerleşimini görür.
   */
  private readInviteFromUrl(): boolean {
    const q = new URLSearchParams(location.search);
    const seed = q.get('seed');
    if (!seed) return false;
    const mode = q.get('mode') === 'story' ? 'story' : 'normal';
    const diff = q.get('diff');
    this.prefill = {
      mode,
      name: (q.get('name') ?? '').slice(0, 24) || (mode === 'story' ? 'KG Vadisi' : 'Paylaşılan Dünya'),
      seed,
      difficulty: difficulties.has(diff ?? '') ? (diff as string) : 'normal',
    };
    return true;
  }

  // ---------------------------------------------------------------- dünya listesi

  private buildWorlds(): void {
    const list = h('div', { class: 'world-list', id: 'world-list' });
    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen('mode'));
    this.screen('worlds', [h('h1', { text: 'DÜNYALARIM' }), list, back]);
  }

  private async renderWorlds(): Promise<void> {
    const list = this.screens.get('worlds')!.querySelector('#world-list') as HTMLElement;
    clear(list);

    // Bulut durumu satırı
    const cloudLine = h('div', { class: 'meta', style: 'text-align:left;width:100%' });
    const setCloudLine = () => {
      const c = this.game.cloud;
      const map: Record<string, string> = {
        disabled: '☁️ Bulut kaydı kapalı',
        offline: '☁️ Çevrimdışı — yerel kayıt sürüyor',
        connecting: '☁️ Bağlanıyor…',
        ready: '☁️ Bulut kaydı açık',
        syncing: '☁️ Eşitleniyor…',
        conflict: '⚠️ Çakışma var, aşağıdan seç',
        quota: '☁️ Günlük kota doldu — yerel kayıt sürüyor',
        error: '☁️ Bulut hatası — yerel kayıt sürüyor',
      };
      cloudLine.textContent = map[c.status] ?? c.status;
    };
    setCloudLine();
    this.game.cloud.events.on('status:changed', setCloudLine);
    list.append(cloudLine);

    const localWorlds = this.game.saves.listWorlds();
    const cloudWorlds = await this.game.cloud.list();
    const worlds = this.game.cloud.mergeWorldLists(localWorlds, cloudWorlds);

    if (worlds.length === 0) {
      list.append(h('div', { class: 'empty', text: 'Henüz kayıtlı dünya yok. Yeni bir dünya oluştur.' }));
      return;
    }
    for (const w of worlds) {
      const play = h('button', { class: 'btn small primary', text: 'GİR', type: 'button' });
      onTap(play, () => {
        if (this.game.loadWorld(w.id)) this.showScreen(null);
      });
      const del = h('button', { class: 'btn small danger', text: 'SİL', type: 'button' });
      onTap(del, () => {
        if (!confirm(`"${w.name}" silinsin mi? Bu geri alınamaz.`)) return;
        this.game.saves.deleteWorld(w.id);
        void this.game.cloud.remove(w.id);
        void this.renderWorlds();
      });
      // Paylaş: aynı arazi/kaynak yerleşimini isteyen arkadaşın için bağlantı üretir.
      // (Dünya ilerlemesi paylaşılmaz — kayıtlar cihaza özeldir.)
      const share = h('button', { class: 'btn small ghost', text: 'PAYLAŞ', type: 'button' });
      onTap(share, () => void this.shareWorld(w));

      const diff = difficulties.get(w.difficulty)?.name ?? w.difficulty;

      // Buluttan indir (yerelde yoksa)
      const pull = h('button', { class: 'btn small primary', text: 'İNDİR', type: 'button' });
      onTap(pull, () => {
        void this.game.loadFromCloud(w.id).then((ok) => {
          if (ok) this.showScreen(null);
        });
      });

      // Çakışma çözümü
      const useCloud = h('button', { class: 'btn small', text: 'BULUTU AL', type: 'button' });
      onTap(useCloud, () => {
        void this.game.resolveCloudConflict(w.id, 'cloud').then(() => void this.renderWorlds());
      });
      const useLocal = h('button', { class: 'btn small danger', text: 'YERELİ GÖNDER', type: 'button' });
      onTap(useLocal, () => {
        void this.game.resolveCloudConflict(w.id, 'local').then(() => void this.renderWorlds());
      });

      const badge = w.onlyCloud ? ' ☁️ yalnızca bulutta' : w.cloud ? ' ☁️ yedekli' : ' 📱 yalnızca bu cihazda';
      const actions: HTMLElement[] = w.conflict ? [useCloud, useLocal] : w.onlyCloud ? [pull, del] : [share, play, del];

      list.append(
        h('div', { class: 'world-item', style: w.conflict ? 'border-color:#f87171' : '' }, [
          h('div', { class: 'info' }, [
            h('div', { class: 'name', text: w.name + (w.conflict ? ' ⚠️' : '') }),
            h('div', {
              class: 'meta',
              text: `${w.mode === 'story' ? 'Hikâye' : 'Normal'} · ${diff} · Gün ${w.day ?? 1} · ${fmtTime(w.playtime ?? 0)} · seed ${w.seed}${badge}`,
            }),
          ]),
          ...actions,
        ]),
      );
    }
  }

  // ---------------------------------------------------------------- dünya oluştur

  private buildCreate(): void {
    this.screen('create', [h('h1', { text: 'YENİ DÜNYA' }), h('div', { class: 'card', id: 'create-card' })]);
  }

  private renderCreate(): void {
    const card = this.screens.get('create')!.querySelector('#create-card') as HTMLElement;
    clear(card);
    const nameInput = h('input', { type: 'text', maxlength: 24, placeholder: 'Dünya adı', value: this.prefill.name });
    const seedInput = h('input', { type: 'text', maxlength: 32, placeholder: 'Boş bırakırsan rastgele', value: this.prefill.seed ?? '' });
    let difficulty = this.prefill.difficulty ?? 'normal';
    const seg = h('div', { class: 'seg' });
    for (const d of difficulties.all()) {
      const b = h('button', { type: 'button', class: d.id === difficulty ? 'on' : '', text: d.name });
      onTap(b, () => {
        difficulty = d.id;
        for (const child of Array.from(seg.children)) child.classList.remove('on');
        b.classList.add('on');
        descEl.textContent = d.desc;
      });
      seg.append(b);
    }
    const descEl = h('p', { class: 'hint', style: 'text-align:left', text: difficulties.require(difficulty).desc });

    const create = h('button', { class: 'btn primary', text: 'DÜNYAYI OLUŞTUR', type: 'button' });
    onTap(create, () => {
      this.game.createWorld({
        name: nameInput.value || (this.prefill.mode === 'story' ? 'KG Vadisi' : 'Yeni Dünya'),
        mode: this.prefill.mode,
        difficulty,
        seedText: seedInput.value,
      });
      this.showScreen(null);
    });
    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen('mode'));

    card.append(
      h('div', { class: 'field' }, [h('label', { text: 'Dünya adı' }), nameInput]),
      h('div', { class: 'field' }, [h('label', { text: 'Zorluk' }), seg, descEl]),
      h('div', { class: 'field' }, [h('label', { text: 'Dünya seed’i' }), seedInput]),
      h('p', {
        class: 'hint',
        text:
          this.prefill.mode === 'story'
            ? 'Hikâyeli mod: sınırlı, el yapımı harita. Kızıl Kale dünyada yalnızca bir tanedir.'
            : 'Normal mod: chunk tabanlı sınırsız dünya. Aynı seed her zaman aynı dünyayı üretir.',
      }),
      h('div', { class: 'row', style: 'margin-top:8px' }, [create, back]),
    );
  }

  // ---------------------------------------------------------------- ayarlar

  private buildSettings(): void {
    this.screen('settings', [h('h1', { text: 'AYARLAR' }), h('div', { class: 'card', id: 'settings-card' })]);
    this.renderSettings();
  }

  private renderSettings(): void {
    const card = this.screens.get('settings')!.querySelector('#settings-card') as HTMLElement;
    clear(card);
    const s = this.game.settings;

    // Grafik kalitesi
    const qseg = h('div', { class: 'seg' });
    const labels: Record<QualityLevel, string> = { low: 'Düşük', medium: 'Orta', high: 'Yüksek', ultra: 'Çok Yüksek' };
    for (const q of Object.keys(QUALITY_PROFILES) as QualityLevel[]) {
      const b = h('button', { type: 'button', class: s.graphics.quality === q ? 'on' : '', text: labels[q] });
      onTap(b, () => {
        s.patchGraphics({ quality: q });
        this.renderSettings();
      });
      qseg.append(b);
    }

    const slider = (
      label: string,
      value: number,
      min: number,
      max: number,
      step: number,
      onInput: (v: number) => void,
      fmt: (v: number) => string = (v) => String(v),
    ) => {
      const val = h('span', { text: fmt(value) });
      const input = h('input', { type: 'range', min, max, step, value });
      input.addEventListener('input', () => {
        const v = Number(input.value);
        val.textContent = fmt(v);
        onInput(v);
      });
      return h('div', { class: 'field' }, [h('label', {}, [`${label}: `, val]), input]);
    };

    const toggle = (label: string, on: boolean, fn: (v: boolean) => void) => {
      const b = h('button', { class: `btn small ${on ? 'primary' : 'ghost'}`, type: 'button', text: on ? 'AÇIK' : 'KAPALI' });
      onTap(b, () => fn(!on));
      return h('div', { class: 'field' }, [h('label', { text: label }), b]);
    };

    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen(this.game.state === 'playing' ? null : 'menu'));

    card.append(
      h('div', { class: 'field' }, [h('label', { text: 'Grafik kalitesi' }), qseg]),
      slider('Görüş / chunk mesafesi', s.graphics.renderDistance, 2, 12, 1, (v) => s.patchGraphics({ renderDistance: v }), (v) => `${v} chunk`),
      slider('Görüş açısı (FOV)', s.graphics.fov, 50, 120, 1, (v) => s.patchGraphics({ fov: v })),
      slider('Parçacık yoğunluğu', s.graphics.particles, 0, 1, 0.05, (v) => s.patchGraphics({ particles: v }), (v) => `${Math.round(v * 100)}%`),
      toggle('Gölgeler', s.graphics.shadows, (v) => {
        s.patchGraphics({ shadows: v });
        this.renderSettings();
      }),
      h('hr', { style: 'border-color:#2b3947; margin:14px 0' }),
      slider('Ana ses', s.audio.master, 0, 1, 0.05, (v) => s.patchAudio({ master: v }), (v) => `${Math.round(v * 100)}%`),
      slider('Müzik', s.audio.music, 0, 1, 0.05, (v) => s.patchAudio({ music: v }), (v) => `${Math.round(v * 100)}%`),
      slider('Efektler', s.audio.sfx, 0, 1, 0.05, (v) => s.patchAudio({ sfx: v }), (v) => `${Math.round(v * 100)}%`),
      slider('Ortam sesi', s.audio.ambient, 0, 1, 0.05, (v) => s.patchAudio({ ambient: v }), (v) => `${Math.round(v * 100)}%`),
      h('hr', { style: 'border-color:#2b3947; margin:14px 0' }),
      slider('Kamera hassasiyeti', s.controls.cameraSensitivity, 0.2, 3, 0.05, (v) => s.patchControls({ cameraSensitivity: v }), (v) => v.toFixed(2)),
      slider('Dokunma hassasiyeti', s.controls.touchSensitivity, 0.2, 3, 0.05, (v) => s.patchControls({ touchSensitivity: v }), (v) => v.toFixed(2)),
      slider('Buton boyutu', s.controls.buttonScale, 0.7, 1.6, 0.05, (v) => s.patchControls({ buttonScale: v }), (v) => `${Math.round(v * 100)}%`),
      (() => {
        const seg = h('div', { class: 'seg' });
        for (const [id, label] of [['right', 'Sağ el'], ['left', 'Sol el']] as const) {
          const b = h('button', { type: 'button', class: s.controls.layout === id ? 'on' : '', text: label });
          onTap(b, () => {
            s.patchControls({ layout: id });
            this.renderSettings();
          });
          seg.append(b);
        }
        return h('div', { class: 'field' }, [h('label', { text: 'Mobil buton düzeni' }), seg]);
      })(),
      toggle('Y eksenini ters çevir', s.controls.invertY, (v) => {
        s.patchControls({ invertY: v });
        this.renderSettings();
      }),
      h('p', { class: 'hint', style: 'text-align:left', text: `Depolama kullanımı: ${this.game.saves.usageKB()} KB` }),
      h('div', { class: 'row', style: 'margin-top:10px' }, [back]),
    );
  }

  // ---------------------------------------------------------------- profil

  private buildProfile(): void {
    this.screen('profile', [h('h1', { text: 'PROFİL' }), h('div', { class: 'card', id: 'profile-card' })]);
  }

  private renderProfile(): void {
    const card = this.screens.get('profile')!.querySelector('#profile-card') as HTMLElement;
    clear(card);
    const p = this.game.profile;

    const avatar = h('div', {
      style: `width:72px;height:72px;border-radius:50%;border:2px solid #2b3947;background:#0e141b center/cover no-repeat${
        p.avatar ? ` url(${p.avatar})` : ''
      };display:grid;place-items:center;font-size:28px`,
      text: p.avatar ? '' : '🙂',
    });
    const file = h('input', { type: 'file', accept: 'image/*', style: 'display:none' });
    file.addEventListener('change', () => {
      const f = file.files?.[0];
      if (!f) return;
      if (f.size > 3_000_000) {
        this.toast('Fotoğraf çok büyük (max 3 MB)', 'bad');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // Fotoğrafı küçült: kayıt boyutu şişmesin
        const img = new Image();
        img.onload = () => {
          const size = 128;
          const cv = document.createElement('canvas');
          cv.width = size;
          cv.height = size;
          const ctx = cv.getContext('2d');
          if (!ctx) return;
          const scale = Math.max(size / img.width, size / img.height);
          const w = img.width * scale;
          const hh = img.height * scale;
          ctx.drawImage(img, (size - w) / 2, (size - hh) / 2, w, hh);
          p.avatar = cv.toDataURL('image/jpeg', 0.82);
          this.game.saves.saveProfile(p);
          this.renderProfile();
        };
        img.src = String(reader.result);
      };
      reader.readAsDataURL(f);
    });
    const pick = h('button', { class: 'btn small', text: 'Galeriden seç', type: 'button' });
    onTap(pick, () => file.click());

    const nameInput = h('input', { type: 'text', maxlength: 24, value: p.name });
    nameInput.addEventListener('change', () => {
      p.name = nameInput.value.trim() || 'Gezgin';
      this.game.saves.saveProfile(p);
    });

    const st = p.stats;
    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen('menu'));

    // ---------------- HESAP BÖLÜMÜ ----------------
    const accountBox = h('div', { class: 'card', style: 'background:#0e141b;margin-top:6px' });
    const accStatus = h('div', { class: 'stat-line', text: 'Hesap durumu kontrol ediliyor…' });
    accountBox.append(h('h2', { text: '🔐 Hesap' }), accStatus);

    const nameIn = h('input', { type: 'text', maxlength: 24, placeholder: 'Hesap adı (en az 3 karakter)' });
    const passIn = h('input', { type: 'password', maxlength: 64, placeholder: 'Şifre (en az 6 karakter)' });
    const msg = h('p', { class: 'hint', style: 'text-align:left' });

    const regBtn = h('button', { class: 'btn small primary', text: 'HESAP OLUŞTUR', type: 'button' });
    onTap(regBtn, () => {
      void this.game.cloud.register(nameIn.value.trim(), passIn.value).then((r) => {
        msg.textContent = r.message;
        this.toast(r.message, r.ok ? 'good' : 'bad');
        if (r.ok) this.renderProfile();
      });
    });
    const loginBtn = h('button', { class: 'btn small', text: 'GİRİŞ YAP', type: 'button' });
    onTap(loginBtn, () => {
      void this.game.cloud.login(nameIn.value.trim(), passIn.value).then(async (r) => {
        msg.textContent = r.message;
        this.toast(r.message, r.ok ? 'good' : 'bad');
        if (r.ok) {
          const restored = await this.game.restoreCloudWorldsAfterLogin();
          this.renderProfile();
          if (restored.available > 0) {
            await this.renderWorlds();
            this.showScreen('worlds');
          }
        }
      });
    });

    const codeBtn = h('button', { class: 'btn small', text: 'KURTARMA KODU ÜRET', type: 'button' });
    onTap(codeBtn, () => {
      void this.game.cloud.recoveryCode().then((r) => {
        if (r.ok && r.code) {
          msg.innerHTML = `Kodun: <b style="font-size:18px;letter-spacing:2px">${r.code}</b><br><small>Bir kez kullanılabilir. Güvenli bir yere yaz.</small>`;
          void navigator.clipboard?.writeText(r.code).catch(() => {});
          this.toast('Kod üretildi ve kopyalandı', 'good');
        } else {
          msg.textContent = r.message;
          this.toast(r.message, 'bad');
        }
      });
    });
    const codeIn = h('input', { type: 'text', maxlength: 20, placeholder: 'KG-XXXX-XXXX-XXXX' });
    const claimBtn = h('button', { class: 'btn small', text: 'KODLA BAĞLAN', type: 'button' });
    onTap(claimBtn, () => {
      void this.game.cloud.claim(codeIn.value.trim()).then(async (r) => {
        msg.textContent = r.message;
        this.toast(r.message, r.ok ? 'good' : 'bad');
        if (r.ok) {
          const restored = await this.game.restoreCloudWorldsAfterLogin();
          this.renderProfile();
          if (restored.available > 0) {
            await this.renderWorlds();
            this.showScreen('worlds');
          }
        }
      });
    });

    accountBox.append(
      h('div', { class: 'field' }, [h('label', { text: 'Hesap adı' }), nameIn]),
      h('div', { class: 'field' }, [h('label', { text: 'Şifre' }), passIn]),
      h('div', { class: 'row', style: 'justify-content:flex-start' }, [regBtn, loginBtn]),
      h('hr', { style: 'border-color:#2b3947;margin:12px 0' }),
      h('p', { class: 'hint', style: 'text-align:left', text: 'Şifre kullanmak istemiyorsan kurtarma kodu üretip başka cihazda girebilirsin.' }),
      h('div', { class: 'row', style: 'justify-content:flex-start' }, [codeBtn]),
      h('div', { class: 'field', style: 'margin-top:8px' }, [h('label', { text: 'Kodla bağlan' }), codeIn]),
      h('div', { class: 'row', style: 'justify-content:flex-start' }, [claimBtn]),
      msg,
    );

    // Durumu asenkron doldur
    void this.game.cloud.accountStatus().then((acc) => {
      if (!acc) {
        accStatus.textContent = this.game.cloud.status === 'disabled' ? 'Bulut kaydı kapalı: hesap kullanılamaz.' : 'Sunucuya ulaşılamıyor.';
        return;
      }
      if (acc.anonymous) {
        accStatus.innerHTML = '⚠️ <b>Anonim hesap</b> — verin sunucuda ama yalnızca bu cihazdan erişilebilir. Tarayıcı verisini silersen kaybolur. Hesap oluşturursan başka cihazdan da girebilirsin.';
      } else {
        accStatus.innerHTML = `✅ Giriş yapıldı: <b>${acc.name}</b> — başka cihazdan aynı hesapla girebilirsin.`;
      }
    });

    card.append(
      h('div', { class: 'equip-row' }, [avatar, pick, file]),
      h('div', { class: 'field' }, [h('label', { text: 'Oyuncu adı' }), nameInput]),
      h('div', { class: 'stat-line', html: `
        Oynama süresi: <b>${fmtTime(st.playtime)}</b><br>
        Keşfedilen bölge: <b>${st.discovered}</b><br>
        Oluşturulan dünya: <b>${st.worldsCreated}</b><br>
        Çıkarılan maden: <b>${st.oresMined}</b> · Tutulan balık: <b>${st.fishCaught}</b><br>
        Yenilen boss: <b>${st.bossesKilled}</b> · Ölüm: <b>${st.deaths}</b>
      ` }),
      accountBox,
      h('h2', { text: `Başarımlar (${p.achievements.length})` }),
      h('div', { class: 'stat-line', text: p.achievements.length ? p.achievements.join(', ') : 'Henüz başarım yok.' }),
      h('div', { class: 'row' }, [back]),
    );
  }

  // ---------------------------------------------------------------- sorun bildir

  private buildReport(): void {
    const ta = h('textarea', { rows: 6, placeholder: 'Sorunu olabildiğince ayrıntılı yaz...' });
    const send = h('button', { class: 'btn primary', text: 'GÖNDER', type: 'button' });
    const status = h('p', { class: 'hint' });
    onTap(send, () => {
      const text = ta.value.trim();
      if (text.length < 8) {
        status.textContent = 'Lütfen biraz daha ayrıntı yaz.';
        return;
      }
      // Modüler gönderim: şimdilik yerelde birikir, backend eklendiğinde
      // yalnızca bu fonksiyonun gövdesi değişir.
      const report = {
        text,
        at: new Date().toISOString(),
        ua: navigator.userAgent,
        state: this.game.state,
        world: this.game.meta?.id ?? null,
        fps: Math.round(this.game.fps),
      };
      try {
        const key = 'kg.reports';
        const list = JSON.parse(localStorage.getItem(key) ?? '[]');
        list.push(report);
        localStorage.setItem(key, JSON.stringify(list.slice(-20)));
        status.textContent = 'Teşekkürler, bildirim kaydedildi.';
        ta.value = '';
      } catch {
        status.textContent = 'Bildirim kaydedilemedi (depolama dolu olabilir).';
      }
    });
    const back = h('button', { class: 'btn ghost', text: 'GERİ', type: 'button' });
    onTap(back, () => this.showScreen(this.game.state === 'playing' ? null : 'menu'));
    this.screen('report', [
      h('h1', { text: 'SORUN BİLDİR' }),
      h('div', { class: 'card' }, [h('div', { class: 'field' }, [h('label', { text: 'Ne oldu?' }), ta]), status, h('div', { class: 'row' }, [send, back])]),
    ]);
  }

  // ---------------------------------------------------------------- ölüm

  private buildDead(): void {
    const respawn = h('button', { class: 'btn primary', text: 'YENİDEN DOĞ', type: 'button' });
    onTap(respawn, () => {
      const p = this.game.player;
      const w = this.game.world;
      if (!p || !w) return;
      p.stats.reset();
      p.placeAt(w, 0, 0);
      this.game.setState('playing');
      this.showScreen(null);
      this.game.saveNow();
    });
    const menu = h('button', { class: 'btn', text: 'ANA MENÜ', type: 'button' });
    onTap(menu, () => {
      this.game.exitToMenu();
      this.showScreen('menu');
    });
    this.screen('dead', [h('h1', { text: 'ÖLDÜN' }), h('p', { class: 'hint cause' }), h('div', { class: 'row' }, [respawn, menu])]);
  }

  // ---------------------------------------------------------------- HUD

  private buildHud(): void {
    const bar = (cls: string) => {
      const fill = h('i');
      const label = h('span');
      const el = h('div', { class: `bar ${cls}` }, [fill, label]);
      return { el, fill, label };
    };
    const hp = bar('hp');
    const food = bar('food');
    const stam = bar('stam');
    this.hudRefs.hpFill = hp.fill;
    this.hudRefs.hpLabel = hp.label;
    this.hudRefs.foodFill = food.fill;
    this.hudRefs.foodLabel = food.label;
    this.hudRefs.stamFill = stam.fill;
    this.hudRefs.stamLabel = stam.label;

    const clockEl = h('div', { class: 'hud-clock' });
    this.hudRefs.clock = clockEl;
    const observerBadge = h('div', { class: 'effect', text: 'YÖNETİCİ GÖZLEMCİ · SALT OKUNUR', style: 'display:none;background:#b45309;color:white;font-weight:700' });
    this.hudRefs.observerBadge = observerBadge;
    const effects = h('div', { class: 'effects' });
    this.hudRefs.effects = effects;
    const aim = h('div', { class: 'aim-info' });
    this.hudRefs.aim = aim;

    const invBtn = h('button', { class: 'icon-btn', text: '🎒', type: 'button', 'aria-label': 'Envanter' });
    onTap(invBtn, () => this.toggleModal('inventory'));
    const craftBtn = h('button', { class: 'icon-btn', text: '🛠️', type: 'button', 'aria-label': 'Üretim' });
    onTap(craftBtn, () => {
      this.craftStation = 'hand';
      this.toggleModal('craft');
    });
    const rotBtn = h('button', { class: 'icon-btn', text: '⟳', type: 'button', 'aria-label': 'Yapıyı döndür' });
    onTap(rotBtn, () => {
      this.game.rotateBuild();
      this.toast('Yapı döndürüldü');
    });
    const pauseBtn = h('button', { class: 'icon-btn', text: '⏸', type: 'button', 'aria-label': 'Duraklat' });
    onTap(pauseBtn, () => this.openModal('pause'));

    this.hotbarEl = h('div', { id: 'hotbar', role: 'toolbar', 'aria-label': 'Hızlı erişim' });
    for (const event of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'click']) {
      this.hotbarEl.addEventListener(event, (e) => e.stopPropagation());
    }
    this.toasts = h('div', { id: 'toasts' });

    const hurtVignette = h('div', { id: 'hurt-vignette' });
    this.hudRefs.hurtVignette = hurtVignette;
    const crosshair = h('div', { id: 'crosshair' });
    const hitMarker = h('div', { id: 'hit-marker' });
    this.hudRefs.hitMarker = hitMarker;

    this.hud = h('div', { id: 'hud' }, [
      h('div', { class: 'hud-top' }, [h('div', {}, [clockEl, observerBadge]), h('div', { class: 'bars' }, [hp.el, food.el, stam.el])]),
      h('div', { class: 'hud-right' }, [rotBtn, craftBtn, invBtn, pauseBtn]),
      effects,
      crosshair,
      hitMarker,
      aim,
      this.hotbarEl,
      this.toasts,
      hurtVignette,
    ]);
    this.root.append(this.hud);
    this.updateHotbar();
  }

  private hurtVignetteTimer: ReturnType<typeof setTimeout> | null = null;
  /** Oyuncu hasar aldığında ekran kenarında kısa kırmızı vinyet flaşı. */
  private flashHurt(): void {
    const el = this.hudRefs.hurtVignette;
    if (!el) return;
    el.classList.remove('flash');
    // Aynı karede remove+add yapılırsa animasyon yeniden tetiklenmeyebilir (CSS reflow gerekir).
    void el.offsetWidth;
    el.classList.add('flash');
    if (this.hurtVignetteTimer) clearTimeout(this.hurtVignetteTimer);
    this.hurtVignetteTimer = setTimeout(() => el.classList.remove('flash'), 450);
  }

  private hpPulseTimer: ReturnType<typeof setTimeout> | null = null;
  /** Can barında kısa bir "pulse" (büyüyüp normale dönme) — darbenin ani hissini verir. */
  private pulseHpBar(): void {
    const fill = this.hudRefs.hpFill;
    const bar = fill?.parentElement;
    if (!bar) return;
    bar.classList.remove('pulse');
    void bar.offsetWidth;
    bar.classList.add('pulse');
    if (this.hpPulseTimer) clearTimeout(this.hpPulseTimer);
    this.hpPulseTimer = setTimeout(() => bar.classList.remove('pulse'), 300);
  }

  private hitMarkerTimer: ReturnType<typeof setTimeout> | null = null;
  /** Canlıya vuruş anında nişangahta kısa bir "hit marker" (isabet işareti) flaşı. */
  private flashHitMarker(killed: boolean): void {
    const el = this.hudRefs.hitMarker;
    if (!el) return;
    el.classList.remove('show', 'kill');
    void el.offsetWidth;
    el.classList.add('show');
    if (killed) el.classList.add('kill');
    if (this.hitMarkerTimer) clearTimeout(this.hitMarkerTimer);
    this.hitMarkerTimer = setTimeout(() => el.classList.remove('show', 'kill'), 260);
  }

  private updateHud(): void {
    const p = this.game.player;
    if (!p) return;
    const st = p.stats;
    const set = (fill: string, label: string, v: number, max: number, text: string) => {
      const f = this.hudRefs[fill];
      const l = this.hudRefs[label];
      if (f) f.style.width = `${Math.max(0, Math.min(100, (v / max) * 100))}%`;
      if (l) l.textContent = text;
    };
    set('hpFill', 'hpLabel', st.health, st.maxHealth, `${Math.ceil(st.health)} / ${st.maxHealth}`);
    set('foodFill', 'foodLabel', st.hunger, MAX_HUNGER, `Açlık ${Math.ceil(st.hunger)}`);
    set('stamFill', 'stamLabel', st.stamina, MAX_STAMINA, `Stamina ${Math.ceil(st.stamina)}`);

    const clock = this.hudRefs.clock;
    if (clock) {
      const biome = this.game.world?.biomeAt(p.position.x, p.position.z);
      clock.innerHTML = `<b>Gün ${this.game.clock.day}</b> ${fmtClock(this.game.clock.hour)} · ${this.game.clock.phaseLabel()} · ${
        biome?.biome.name ?? ''
      } · 💰 ${Math.floor(p.kogox)} · ${Math.round(this.game.fps)} FPS`;
    }
    if (this.hudRefs.observerBadge) this.hudRefs.observerBadge.style.display = this.game.isObserver ? 'inline-block' : 'none';

    const eff = this.hudRefs.effects;
    if (eff) {
      clear(eff);
      for (const e of st.activeEffects()) {
        eff.append(h('div', { class: 'effect', text: `${effectLabel(e.id)} ${Math.ceil(e.remaining)}s` }));
      }
      const cd = this.game.powerCooldownRemaining;
      if (cd > 0) eff.append(h('div', { class: 'effect', text: `ÖZ GÜÇ ${cd.toFixed(1)}s` }));
    }

    const aim = this.hudRefs.aim;
    if (aim) {
      const info = this.game.aimInfo;
      if (info) {
        aim.innerHTML = '';
        aim.append(h('div', { text: info.name }));
        if (info.progress > 0) {
          const bar = h('div', { class: 'pbar' }, [h('i', { style: `width:${Math.round(info.progress * 100)}%` })]);
          aim.append(bar);
        }
      } else {
        aim.innerHTML = '';
      }
    }
  }

  private updateHotbar(): void {
    const p = this.game.player;
    clear(this.hotbarEl);
    if (!p) return;
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const slot = p.inventory.slots[i] ?? null;
      const el = this.slotEl(slot, i === p.inventory.selected, String(i + 1));
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', `Hızlı erişim ${i + 1}`);
      el.setAttribute('aria-pressed', i === p.inventory.selected ? 'true' : 'false');
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.game.isObserver) p.inventory.select(i);
      });
      onTap(el, (e) => {
        e.stopPropagation();
        if (!this.game.isObserver) p.inventory.select(i);
      });
      this.hotbarEl.append(el);
    }
  }

  private slotEl(slot: Slot | null, active: boolean, key?: string): HTMLElement {
    const def = slot ? itemDef(slot.item) : null;
    const children: Array<Node | string> = [];
    if (def) children.push(document.createTextNode(def.icon));
    if (slot?.broken) children.push(h('span', { class: 'n', text: '⚠' }));
    if (slot && slot.count > 1) children.push(h('span', { class: 'n', text: String(slot.count) }));
    if (key) children.push(h('span', { class: 'k', text: key }));
    if (slot?.dur !== undefined && def) {
      const max = def.tool?.durability ?? def.armor?.durability ?? 1;
      const pct = Math.max(0, Math.min(100, (slot.dur / max) * 100));
      const color = pct > 50 ? '#4ade80' : pct > 20 ? '#fbbf24' : '#f87171';
      children.push(h('div', { class: 'dur' }, [h('i', { style: `width:${pct}%;background:${color}` })]));
    }
    const el = h('div', { class: `slot${active ? ' on' : ''}`, title: def ? `${def.name}${slot?.dur !== undefined ? ` (${slot.dur})` : ''}` : '' }, children);
    return el;
  }

  // ---------------------------------------------------------------- modal

  private buildModal(): void {
    this.modalCard = h('div', { class: 'modal-card' });
    this.modal = h('div', { class: 'modal' }, [this.modalCard]);
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    this.root.append(this.modal);
  }

  toggleModal(kind: 'inventory' | 'craft'): void {
    if (this.currentModal === kind) this.closeModal();
    else this.openModal(kind);
  }

  openModal(kind: 'inventory' | 'craft' | 'hammer' | 'pause' | 'chest' | 'shop'): void {
    if (this.game.isObserver && kind !== 'pause') return;
    this.currentModal = kind;
    this.modal.classList.add('active');
    this.game.input.setEnabled(false);
    this.game.touch.setVisible(false);
    if (this.game.state === 'playing') this.game.setState('paused');
    this.renderModal();
  }

  closeModal(): void {
    this.currentModal = 'none';
    this.modal.classList.remove('active');
    this.hammerContext = null;
    this.chestId = null;
    this.pendingChest = null;
    // Izgarada kalan eşyaları envantere geri ver (kayıp olmasın)
    const p = this.game.player;
    if (p) {
      for (let i = 0; i < this.craftGrid.length; i++) {
        const c = this.craftGrid[i];
        if (c) {
          p.inventory.add(c.item, c.count);
          this.craftGrid[i] = null;
        }
      }
    }
    if (this.game.state === 'paused') this.game.setState('playing');
  }

  private renderModal(): void {
    clear(this.modalCard);
    const title =
      this.currentModal === 'inventory'
        ? 'ENVANTER'
        : this.currentModal === 'craft'
          ? this.craftStation === 'workbench'
            ? 'ÜRETİM MASASI (8×8)'
            : this.craftStation === 'furnace'
              ? 'FIRIN'
              : this.craftStation === 'grinder'
                ? 'KUM ÖĞÜTÜCÜ'
                : 'ÜRETİM'
          : this.currentModal === 'hammer'
            ? 'YAPI DÜZENLE'
            : this.currentModal === 'chest'
              ? 'SANDIK'
              : this.currentModal === 'shop'
                ? 'BEDO’NUN DÜKKÂNI'
                : 'DURAKLATILDI';
    const close = h('button', { class: 'btn small danger', text: '✕', type: 'button' });
    onTap(close, () => this.closeModal());
    this.modalCard.append(h('div', { class: 'modal-head' }, [h('h3', { text: title }), close]));
    const body = h('div', { class: 'modal-body', id: 'modal-body' });
    this.modalCard.append(body);
    this.renderModalBody();
  }

  private renderModalBody(): void {
    const body = this.modalCard.querySelector('#modal-body') as HTMLElement | null;
    if (!body) return;
    clear(body);
    switch (this.currentModal) {
      case 'inventory':
        this.renderInventory(body);
        break;
      case 'craft':
        this.renderCraft(body);
        break;
      case 'hammer':
        this.renderHammer(body);
        break;
      case 'pause':
        this.renderPause(body);
        break;
      case 'shop':
        this.renderShop(body);
        break;
      case 'chest':
        this.renderChest(body);
        break;
      default:
        break;
    }
  }

  private renderInventory(body: HTMLElement): void {
    const p = this.game.player;
    if (!p) return;
    const inv = p.inventory;

    // Ekipman
    const equipRow = h('div', { class: 'equip-row' });
    for (const slot of ['head', 'chest', 'legs', 'feet'] as ArmorSlot[]) {
      const s = inv.equipment[slot];
      const el = this.slotEl(s, false);
      onTap(el, () => {
        if (inv.unequip(slot)) this.renderModalBody();
      });
      equipRow.append(el);
    }
    equipRow.append(h('div', { class: 'stat-line', text: `Savunma: ${inv.defense()}${inv.hasFullSet('power') ? ' · ÖZ GÜÇ hazır (Q)' : ''}` }));
    body.append(h('h3', { text: 'Ekipman', style: 'margin:0 0 8px' }), equipRow);

    // Çanta
    const grid = h('div', { class: 'grid-inv' });
    for (let i = 0; i < inv.slots.length; i++) {
      const s = inv.slots[i] ?? null;
      const el = this.slotEl(s, this.pendingMove === i, i < HOTBAR_SIZE ? String(i + 1) : undefined);
      onTap(el, () => {
        if (this.pendingMove === null) {
          if (!s) return;
          // Zırh ise doğrudan kuşan
          if (itemDef(s.item).armor) {
            inv.equip(i);
            this.renderModalBody();
            return;
          }
          // Yenilebilir ise ye
          const food = itemDef(s.item).food;
          const potion = itemDef(s.item).potion;
          if (food || potion) {
            if (food) p.stats.eat(food.hunger, food.health ?? 0);
            for (const e of potion?.effects ?? []) p.stats.addEffect(e);
            for (const e of food?.effects ?? []) p.stats.addEffect(e);
            inv.removeAt(i, 1);
            this.toast(`${itemDef(s.item).name} kullanıldı`, 'good');
            this.game.audio.eat();
            this.renderModalBody();
            return;
          }
          this.pendingMove = i;
        } else {
          inv.swap(this.pendingMove, i);
          this.pendingMove = null;
        }
        this.renderModalBody();
      });
      grid.append(el);
    }
    body.append(h('h3', { text: 'Çanta', style: 'margin:12px 0 8px' }), grid);

    // Onarım: hasarlı/kırık eşyalar ayrı listede. Düğme daha önce 52px'lik
    // yuvanın içine konuyordu; kutudan taşıp dokunulamaz hâle geliyordu.
    this.renderRepairSection(body, inv);

    body.append(
      h('p', {
        class: 'hint',
        style: 'text-align:left;margin-top:10px',
        text: 'Yiyecek/iksire dokun → kullan. Zırha dokun → kuşan. Diğer eşyalarda: bir yuvaya dokun, sonra hedefe dokun (taşı/birleştir).',
      }),
    );
  }

  /** Hasarlı ve kırık eşyalar için dokunulabilir onarım listesi. */
  private renderRepairSection(body: HTMLElement, inv: Inventory): void {
    const damaged: Array<{ index: number; slot: Slot; max: number }> = [];
    for (let i = 0; i < inv.slots.length; i++) {
      const s = inv.slots[i];
      if (!s || s.dur === undefined) continue;
      const def = itemDef(s.item);
      const max = def.tool?.durability ?? def.armor?.durability;
      if (max === undefined || s.dur >= max) continue;
      damaged.push({ index: i, slot: s, max });
    }
    if (damaged.length === 0) return;

    const list = h('div', { class: 'repair-list' });
    for (const { index, slot, max } of damaged) {
      const def = itemDef(slot.item);
      const material = def.material ?? (def.tool?.class === 'bow' || def.tool?.class === 'rod' ? 'wood' : null);
      const have = material ? inv.count(material) : 0;
      const canRepair = !!material && have >= 1;
      const state = slot.broken ? 'KIRIK' : `${slot.dur}/${max}`;
      const need = material ? `1 ${itemDef(material).name} (var: ${have})` : 'malzeme tanımsız';

      const btn = h('button', { class: 'btn small primary', text: 'TAMİR', type: 'button' });
      (btn as HTMLButtonElement).disabled = !canRepair;
      onTap(btn, () => {
        const result = repairEquipment(inv, index);
        this.toast(result.ok ? `${itemDef(result.item).name} tamir edildi` : result.reason, result.ok ? 'good' : 'bad');
        this.renderModalBody();
      });

      list.append(
        h('div', { class: 'repair-row' }, [
          h('span', { class: 'ico', text: def.icon }),
          h('div', { class: 'repair-info' }, [
            h('div', { class: 'nm', text: `${def.name} · ${state}` }),
            h('div', { class: 'hint', text: `Gerekli: ${need}` }),
          ]),
          btn,
        ]),
      );
    }
    body.append(h('h3', { text: 'Onarım', style: 'margin:14px 0 8px' }), list);
    if (damaged.some((d) => d.slot.broken)) {
      body.append(
        h('p', {
          class: 'hint',
          style: 'text-align:left;margin-top:6px',
          text: 'Kırık alet hasar vermez. Taş gerekiyorsa: yumrukla ağaç kes → odundan kazma üret → kaya kır.',
        }),
      );
    }
  }

  private renderCraft(body: HTMLElement): void {
    const p = this.game.player;
    if (!p) return;
    const station = this.craftStation;

    // Sekmeler: ızgara ve liste
    const gridWrap = h('div', { class: 'craft-wrap' });

    if (station === 'workbench' || station === 'hand') {
      const gridEl = h('div', { class: 'craft-grid' });
      for (let i = 0; i < this.craftGrid.length; i++) {
        const c = this.craftGrid[i];
        const el = this.slotEl(c ? { item: c.item, count: c.count } : null, false);
        onTap(el, () => {
          const cell = this.craftGrid[i];
          if (cell) {
            // Hücreden geri al
            p.inventory.add(cell.item, cell.count);
            this.craftGrid[i] = null;
          } else if (this.pendingMove !== null) {
            const taken = p.inventory.removeAt(this.pendingMove, 1);
            if (taken) this.craftGrid[i] = { item: taken.item, count: taken.count };
            this.pendingMove = null;
          } else {
            this.toast('Önce çantadan bir eşya seç', 'info');
          }
          this.renderModalBody();
        });
        gridEl.append(el);
      }

      const match = findRecipeForGrid(this.craftGrid, station === 'hand' ? 'hand' : 'workbench');
      const outSlot = this.slotEl(match ? { item: match.output.item, count: match.output.count } : null, false);
      const craftBtn = h('button', { class: 'btn small primary', text: 'ÜRET', type: 'button' });
      (craftBtn as HTMLButtonElement).disabled = !match;
      onTap(craftBtn, () => {
        const res = craftFromGrid(p.inventory, this.craftGrid, station === 'hand' ? 'hand' : 'workbench');
        this.toast(res.ok ? `${itemDef(res.item).name} x${res.count} üretildi` : res.reason, res.ok ? 'good' : 'bad');
        this.renderModalBody();
      });

      const side = h('div', { class: 'craft-side' }, [
        h('div', { class: 'craft-out' }, [outSlot, h('div', {}, [h('b', { text: match ? itemDef(match.output.item).name : 'Tarif yok' }), h('br'), h('small', { class: 'hint', text: match?.desc ?? 'Izgaraya doğru deseni yerleştir' })]), craftBtn]),
      ]);
      gridWrap.append(gridEl, side);
      body.append(gridWrap);

      // Çantadan hücreye taşımak için seçim listesi
      const picker = h('div', { class: 'grid-inv', style: 'margin-top:12px' });
      for (let i = 0; i < p.inventory.slots.length; i++) {
        const s = p.inventory.slots[i];
        if (!s) continue;
        const el = this.slotEl(s, this.pendingMove === i);
        onTap(el, () => {
          this.pendingMove = this.pendingMove === i ? null : i;
          this.renderModalBody();
        });
        picker.append(el);
      }
      body.append(h('h3', { text: 'Çanta (seç → ızgaraya dokun)', style: 'margin:14px 0 8px' }), picker);
    }

    // Tek dokunuşla tarif listesi (mobil için asıl kullanışlı yol)
    const list = h('div', { class: 'recipe-list' });
    for (const entry of availableRecipes(p.inventory, station)) {
      const def = itemDef(entry.recipe.output.item);
      const btn = h('button', { class: 'btn small primary', text: 'ÜRET', type: 'button' });
      (btn as HTMLButtonElement).disabled = !entry.craftable;
      onTap(btn, () => {
        const res = craftFromInventory(p.inventory, entry.recipe);
        this.toast(res.ok ? `${itemDef(res.item).name} x${res.count}` : res.reason, res.ok ? 'good' : 'bad');
        this.renderModalBody();
      });
      list.append(
        h('div', { class: `recipe${entry.craftable ? '' : ' no'}` }, [
          h('div', { class: 'ic', text: def.icon }),
          h('div', { class: 't' }, [
            h('b', { text: `${def.name} x${entry.recipe.output.count}` }),
            h('small', { text: entry.craftable ? entry.recipe.category ?? '' : `Eksik: ${entry.missing.join(', ')}` }),
          ]),
          btn,
        ]),
      );
    }
    body.append(h('h3', { text: 'Tarifler', style: 'margin:14px 0 8px' }), list);
  }

  private renderChest(body: HTMLElement): void {
    const p = this.game.player;
    const id = this.chestId;
    const st = id ? this.game.chestOf(id) : null;
    if (!p || !st || !st.storage) {
      body.append(h('p', { class: 'hint', text: 'Sandık artık burada değil.' }));
      return;
    }
    const storage = st.storage;

    body.append(h('p', { class: 'hint', style: 'text-align:left', text: 'Bir yuvaya dokun, sonra hedef yuvaya dokun: taşınır.' }));

    // Sandık ızgarası
    const chestGrid = h('div', { class: 'grid-inv' });
    for (let i = 0; i < storage.length; i++) {
      const s = storage[i] ?? null;
      const el = this.slotEl(s, this.pendingChest === i, undefined);
      onTap(el, () => {
        if (!s) {
          // Boş yuva: bekleyen envanter seçimi varsa buraya bırak
          if (this.pendingMove !== null) {
            if (this.game.chestDeposit(id!, this.pendingMove, i)) this.toast('Sandığa bırakıldı', 'good');
            this.pendingMove = null;
          } else {
            this.toast('Önce çantadan bir eşya seç', 'info');
          }
        } else if (this.pendingChest === null) {
          this.pendingChest = i;
        } else if (this.pendingChest === i) {
          this.pendingChest = null;
        } else {
          // Sandık içinde iki yuva arasında taşıma/birleştirme
          this.game.chestSwap(id!, this.pendingChest, i);
          this.pendingChest = null;
        }
        this.renderModalBody();
      });
      chestGrid.append(el);
    }
    body.append(h('h3', { text: 'Sandık', style: 'margin:8px 0' }), chestGrid);

    // Oyuncu envanteri
    const invGrid = h('div', { class: 'grid-inv' });
    for (let i = 0; i < p.inventory.slots.length; i++) {
      const s = p.inventory.slots[i] ?? null;
      const el = this.slotEl(s, this.pendingMove === i, i < HOTBAR_SIZE ? String(i + 1) : undefined);
      onTap(el, () => {
        if (!s) {
          if (this.pendingChest !== null) {
            if (this.game.chestWithdraw(id!, this.pendingChest, i)) this.toast('Çantaya alındı', 'good');
            this.pendingChest = null;
          }
        } else if (this.pendingMove === null) {
          this.pendingMove = i;
        } else if (this.pendingMove === i) {
          this.pendingMove = null;
        } else {
          p.inventory.swap(this.pendingMove, i);
          this.pendingMove = null;
        }
        this.renderModalBody();
      });
      invGrid.append(el);
    }
    body.append(h('h3', { text: 'Çanta', style: 'margin:12px 0 8px' }), invGrid);

    const takeAll = h('button', { class: 'btn small primary', text: 'HEPSİNİ AL', type: 'button' });
    onTap(takeAll, () => {
      let moved = 0;
      for (let i = 0; i < storage.length; i++) {
        if (storage[i] && this.game.chestWithdraw(id!, i)) moved++;
      }
      this.toast(moved > 0 ? `${moved} yığın alındı` : 'Çanta dolu, alınamadı', moved > 0 ? 'good' : 'bad');
      this.renderModalBody();
    });
    body.append(h('div', { class: 'row', style: 'margin-top:10px' }, [takeAll]));
  }

  private renderHammer(body: HTMLElement): void {
    const ctx = this.hammerContext;
    if (!ctx) return;
    const list = h('div', { class: 'recipe-list' });
    for (const opt of ctx.options) {
      const btn = h('button', { class: `btn small ${opt.kind === 'remove' ? 'danger' : 'primary'}`, text: 'UYGULA', type: 'button' });
      onTap(btn, () => {
        this.game.applyHammerOption(ctx.structureId, opt);
        this.closeModal();
      });
      const costText = (opt.cost ?? []).map((c) => `${itemDef(c.item).name} x${c.count}`).join(', ');
      list.append(
        h('div', { class: 'recipe' }, [
          h('div', { class: 'ic', text: opt.kind === 'remove' ? '🔨' : opt.kind === 'repair' ? '🧰' : '⬆️' }),
          h('div', { class: 't' }, [h('b', { text: opt.label }), h('small', { text: costText || 'Ücretsiz' })]),
          btn,
        ]),
      );
    }
    body.append(h('p', { class: 'hint', style: 'text-align:left', text: 'Yapıyı kırmadan kademe/varyant değiştir, tamir et ya da sök.' }), list);
  }

  private renderPause(body: HTMLElement): void {
    if (this.game.isObserver) {
      const resumeObserver = h('button', { class: 'btn primary', text: 'GÖZLEMEYE DEVAM ET', type: 'button' });
      onTap(resumeObserver, () => this.closeModal());
      const leaveObserver = h('button', { class: 'btn danger', text: 'GÖZLEMDEN ÇIK', type: 'button' });
      onTap(leaveObserver, () => { this.closeModal(); this.game.exitToMenu(); this.showScreen('admin'); void this.renderAdmin(); });
      body.append(h('div', { class: 'card' }, [h('h2', { text: 'YÖNETİCİ GÖZLEMCİ' }), h('p', { class: 'hint', text: 'Salt okunur: hareket ve kamera açık; saldırı, etkileşim, üretim, ticaret ve kayıt kapalı.' }), resumeObserver, leaveObserver]));
      return;
    }
    const resume = h('button', { class: 'btn primary', text: 'DEVAM ET', type: 'button' });
    onTap(resume, () => this.closeModal());
    const save = h('button', { class: 'btn', text: 'KAYDET', type: 'button' });
    onTap(save, () => {
      this.game.saveNow();
      this.toast('Kaydedildi', 'good');
    });
    const settings = h('button', { class: 'btn', text: 'AYARLAR', type: 'button' });
    onTap(settings, () => {
      this.closeModal();
      this.renderSettings();
      this.showScreen('settings');
    });
    const report = h('button', { class: 'btn', text: 'SORUN BİLDİR', type: 'button' });
    onTap(report, () => {
      this.closeModal();
      this.showScreen('report');
    });
    // ---- Çok oyunculu ----
    const mp = this.game.mp;
    const mpBox = h('div', { class: 'card', style: 'background:#0e141b;margin-top:10px' });
    const mpStatus = h('div', { class: 'stat-line' });
    const setMp = () => {
      const map: Record<string, string> = {
        off: 'Kapalı',
        connecting: 'Bağlanıyor…',
        connected: `Bağlı · oda ${mp.roomCode} · ${mp.playerCount} oyuncu`,
        closed: 'Bağlantı kapandı',
        error: 'Hata',
        full: 'Oda dolu',
      };
      mpStatus.innerHTML = `Durum: <b>${map[mp.status] ?? mp.status}</b>${mp.statusMessage ? `<br><small>${mp.statusMessage}</small>` : ''}`;
    };
    setMp();
    this.game.events.on('mp:status', setMp);

    const hostBtn = h('button', { class: 'btn small primary', text: 'ODA KUR', type: 'button' });
    onTap(hostBtn, () => {
      const code = this.game.hostRoom();
      if (code) {
        void navigator.clipboard?.writeText(code).catch(() => {});
        this.toast(`Oda kodu: ${code} (kopyalandı)`, 'good');
        setMp();
      }
    });
    const codeIn = h('input', { type: 'text', maxlength: 64, placeholder: 'ODA KODU' });
    const joinBtn = h('button', { class: 'btn small', text: 'KATIL', type: 'button' });
    onTap(joinBtn, () => {
      const c = codeIn.value.trim().toUpperCase();
      if (c.length < 4) {
        this.toast('Geçerli bir oda kodu gir', 'bad');
        return;
      }
      this.game.joinRoom(c);
      this.closeModal();
    });
    const leaveBtn = h('button', { class: 'btn small danger', text: 'ODADAN ÇIK', type: 'button' });
    onTap(leaveBtn, () => {
      this.game.leaveRoom();
      setMp();
    });
    mpBox.append(
      h('h2', { text: '👥 Ortak Oyun' }),
      mpStatus,
      h('p', { class: 'hint', style: 'text-align:left', text: 'Oda kur, kodu arkadaşına ver. 4 kişiye kadar aynı dünyada oynayabilirsiniz. Kurduğunuz yapılar herkeste görünür.' }),
      h('div', { class: 'row', style: 'justify-content:flex-start' }, [hostBtn, leaveBtn]),
      h('div', { class: 'field', style: 'margin-top:8px' }, [h('label', { text: 'Odaya katıl' }), codeIn]),
      h('div', { class: 'row', style: 'justify-content:flex-start' }, [joinBtn]),
    );

    const exit = h('button', { class: 'btn danger', text: 'KAYDET VE ÇIK', type: 'button' });
    onTap(exit, () => {
      this.closeModal();
      this.game.exitToMenu();
      this.showScreen('menu');
    });
    body.append(
      h('div', { class: 'row', style: 'flex-direction:column;align-items:stretch' }, [resume, save, settings, report, exit]),
      mpBox,
      h('p', { class: 'hint', style: 'margin-top:10px', text: 'PC: WASD hareket · Shift koş · C eğil · Space zıpla · E etkileşim · Q özel güç · I envanter · 1-8 hızlı slot' }),
    );
  }

  private shopTab: 'buy' | 'sell' | 'quests' = 'quests';

  private renderShop(body: HTMLElement): void {
    const p = this.game.player;
    const w = this.game.world;
    if (!p || !w) return;
    this.game.refreshStock();

    body.append(
      h('p', { class: 'hint', style: 'text-align:left', text: '"Altın dişimle yeri göğü aydınlatıyorum patron!"' }),
      h('div', { class: 'stat-line', html: `Kesende: <b>${Math.floor(p.kogox)} Kogox</b> · Gün ${this.game.clock.day}` }),
    );

    const tabs = h('div', { class: 'tabs', style: 'padding:0 0 10px' });
    for (const [key, label] of [['quests', '📜 Görevler'], ['buy', '🛒 Satın Al'], ['sell', '💰 Sat']] as const) {
      const b = h('button', { type: 'button', class: this.shopTab === key ? 'on' : '', text: label });
      onTap(b, () => {
        this.shopTab = key;
        this.renderModalBody();
      });
      tabs.append(b);
    }
    body.append(tabs);

    const list = h('div', { class: 'recipe-list' });

    if (this.shopTab === 'quests') {
      const qs = this.game.quests;
      if (!qs || qs.state.quests.length === 0) {
        list.append(h('div', { class: 'empty', text: 'Bugün görev yok.' }));
      }
      for (const q of qs?.state.quests ?? []) {
        const prog = qs!.progress(q, p, w.discoveredCount);
        const can = qs!.canComplete(q, p, w.discoveredCount);
        const btn = h('button', { class: 'btn small primary', text: q.done ? 'TESLİM EDİLDİ' : 'TESLİM ET', type: 'button' });
        (btn as HTMLButtonElement).disabled = q.done || !can;
        onTap(btn, () => {
          this.game.completeQuest(q.id);
          this.renderModalBody();
        });
        const reward = `+${q.rewardKogox} Kogox` + (q.rewardItems.length ? ' · ' + q.rewardItems.map((r) => `${itemDef(r.item).name} x${r.count}`).join(', ') : '');
        list.append(
          h('div', { class: `recipe${q.done ? ' no' : ''}` }, [
            h('div', { class: 'ic', text: q.done ? '✅' : '📜' }),
            h('div', { class: 't' }, [
              h('b', { text: q.text }),
              h('small', { text: `${Math.min(prog, q.amount)}/${q.amount} · ${reward}` }),
            ]),
            btn,
          ]),
        );
      }
    } else if (this.shopTab === 'buy') {
      for (let i = 0; i < this.game.stock.length; i++) {
        const entry = this.game.stock[i]!;
        const def = itemDef(entry.item);
        const btn = h('button', { class: 'btn small primary', text: `${entry.price} 🪙`, type: 'button' });
        (btn as HTMLButtonElement).disabled = entry.count <= 0 || p.kogox < entry.price;
        onTap(btn, () => {
          this.game.buyFromShop(i, 1);
          this.renderModalBody();
        });
        list.append(
          h('div', { class: `recipe${entry.count <= 0 ? ' no' : ''}` }, [
            h('div', { class: 'ic', text: def.icon }),
            h('div', { class: 't' }, [h('b', { text: def.name }), h('small', { text: `Stok: ${entry.count}` })]),
            btn,
          ]),
        );
      }
      if (this.game.stock.length === 0) list.append(h('div', { class: 'empty', text: 'Stok boş. Yarın gel.' }));
    } else {
      const sellable = this.game.sellList();
      if (sellable.length === 0) list.append(h('div', { class: 'empty', text: 'Satılacak bir şeyin yok.' }));
      for (const entry of sellable) {
        const def = itemDef(entry.item);
        const one = h('button', { class: 'btn small', text: `Sat 1 (+${entry.unit})`, type: 'button' });
        onTap(one, () => {
          this.game.sellToShop(entry.item, 1);
          this.renderModalBody();
        });
        const all = h('button', { class: 'btn small primary', text: `Tümü (+${entry.unit * entry.count})`, type: 'button' });
        onTap(all, () => {
          this.game.sellToShop(entry.item, entry.count);
          this.renderModalBody();
        });
        list.append(
          h('div', { class: 'recipe' }, [
            h('div', { class: 'ic', text: def.icon }),
            h('div', { class: 't' }, [h('b', { text: def.name }), h('small', { text: `Elinde: ${entry.count} · birim ${entry.unit} Kogox` })]),
            one,
            all,
          ]),
        );
      }
    }
    body.append(list);
  }

  // ---------------------------------------------------------------- toast

  toast(text: string, kind: 'info' | 'good' | 'bad' = 'info'): void {
    const el = h('div', { class: `toast ${kind}`, text });
    this.toasts.append(el);
    window.setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity 0.25s';
      window.setTimeout(() => el.remove(), 260);
    }, 1900);
    // Ekranı doldurmasın
    while (this.toasts.childElementCount > 4) this.toasts.firstElementChild?.remove();
  }
}

function effectLabel(id: string): string {
  const map: Record<string, string> = {
    speed: '⚡ Hız',
    strength: '💪 Güç',
    nightvision: '👁️ Gece Görüşü',
    mining: '⛏️ Madencilik',
    venomResist: '🛡️ Zehir Direnci',
    fishLuck: '🎣 Balık Şansı',
    satiety: '🍞 Tokluk',
    venom: '🟣 Zehir',
    cold: '❄️ Soğuk',
    slow: '🐌 Yavaşlık',
  };
  return map[id] ?? id;
}
